import { getSystemSettings, SystemSettings } from "../../../utils/commissionConfig";
import { BookingFinancialBreakdown } from "../types/financial.types";
import { sanitizePixKey } from "./financialValidation";

/**
 * Computes EMV CRC16 CCITT for standard Banco Central do Brasil Pix BR Code / Copia e Cola
 */
export function computeCRC16(str: string): string {
  let crc = 0xffff;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff;
      } else {
        crc = (crc << 1) & 0xffff;
      }
    }
  }
  return (crc & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

/**
 * Generates dynamic fallback Pix BR Code when gateway API is in simulation mode or offline preview.
 * Fully compliant with Banco Central do Brasil (BCB) EMV BR Code specification.
 */
export function generateFallbackPixPayload(
  pixKey: string,
  amount: number,
  merchantName: string = "ARENA ESPORTIVA",
  merchantCity: string = "SAO PAULO",
  txid: string = "***"
): { qrCode: string; qrCodeBase64: string } {
  const settings = getSystemSettings();
  const rawKey = (pixKey || settings.masterPixKey || "").trim();
  const cleanKey = sanitizePixKey(rawKey, settings.pixKeyType);

  if (!cleanKey) {
    const errorNotice = "⚠️ Nenhuma Chave Pix cadastrada no Painel Admin ou Perfil da Arena. Por favor, cadastre sua Chave Pix no Painel Admin (Aba Bancos).";
    return {
      qrCode: errorNotice,
      qrCodeBase64: "",
    };
  }
  const formattedAmount = amount > 0 ? amount.toFixed(2) : "0.00";
  
  // Format merchant name (no special characters/accents, max 25 chars, uppercase)
  const cleanMerchant = merchantName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .substring(0, 25)
    .toUpperCase() || "ARENA ESPORTIVA";

  // Format city (no accents, max 15 chars, uppercase)
  const cleanCity = merchantCity
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .substring(0, 15)
    .toUpperCase() || "SAO PAULO";

  const cleanTxid = txid === "***" ? "***" : (txid.replace(/[^a-zA-Z0-9]/g, "").substring(0, 25) || "***");

  // Field 00: Payload Format Indicator
  const field00 = "000201";

  // Field 26: Merchant Account Information
  const keyLen = cleanKey.length.toString().padStart(2, "0");
  const field26Content = `0014br.gov.bcb.pix01${keyLen}${cleanKey}`;
  const field26Len = field26Content.length.toString().padStart(2, "0");
  const field26 = `26${field26Len}${field26Content}`;

  // Field 52: Merchant Category Code -> '52040000'
  const field52 = "52040000";

  // Field 53: Currency BRL (986) -> '5303986'
  const field53 = "5303986";

  // Field 54: Transaction Amount
  let field54 = "";
  if (amount > 0) {
    const amtLen = formattedAmount.length.toString().padStart(2, "0");
    field54 = `54${amtLen}${formattedAmount}`;
  }

  // Field 58: Country Code -> '5802BR'
  const field58 = "5802BR";

  // Field 59: Merchant Name
  const merLen = cleanMerchant.length.toString().padStart(2, "0");
  const field59 = `59${merLen}${cleanMerchant}`;

  // Field 60: Merchant City
  const cityLen = cleanCity.length.toString().padStart(2, "0");
  const field60 = `60${cityLen}${cleanCity}`;

  // Field 62: Additional Data (txid)
  const txidSubfieldLen = cleanTxid.length.toString().padStart(2, "0");
  const field62Content = `05${txidSubfieldLen}${cleanTxid}`;
  const field62Len = field62Content.length.toString().padStart(2, "0");
  const field62 = `62${field62Len}${field62Content}`;

  // Field 63: CRC16 header -> '6304'
  const field63Header = "6304";

  const rawPayload = `${field00}${field26}${field52}${field53}${field54}${field58}${field59}${field60}${field62}${field63Header}`;
  const checksum = computeCRC16(rawPayload);
  const fullPayload = `${rawPayload}${checksum}`;

  const qrCodeBase64 = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fullPayload)}&bgcolor=18181b&color=34d399`;

  return {
    qrCode: fullPayload,
    qrCodeBase64,
  };
}

/**
 * Format any QR Code string into a valid img src (data: image or http/https URL)
 */
export function formatQrCodeImageSrc(qrCodeBase64?: string, qrCodePayload?: string): string {
  if (qrCodeBase64) {
    const trimmed = qrCodeBase64.trim();
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
      return trimmed;
    }
    return `data:image/png;base64,${trimmed}`;
  }
  if (qrCodePayload) {
    const trimmedPayload = qrCodePayload.trim();
    // Se o payload for uma mensagem de aviso/erro (ex: contém "⚠️", "Nenhuma Chave", "Error", "Erro"), NÃO gerar URL de imagem QR
    if (
      trimmedPayload.includes("⚠️") ||
      trimmedPayload.includes("Nenhuma Chave") ||
      trimmedPayload.toLowerCase().startsWith("erro") ||
      trimmedPayload.toLowerCase().startsWith("error")
    ) {
      return "";
    }
    return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(trimmedPayload)}&bgcolor=18181b&color=34d399`;
  }
  return "";
}

export interface CanonicalPixQrData {
  encodedImage: string;
  payload: string;
  qrCodeBase64: string;
}

/**
 * Extrai canonicamente os dados do QR Code Pix (encodedImage, payload copia-e-cola e qrCodeBase64)
 * de qualquer estrutura de retorno do Asaas ou backend unificado:
 * 1. raiz: response.encodedImage, response.payload
 * 2. objeto pixQrCode: response.pixQrCode.encodedImage, response.pixQrCode.payload
 * 3. objeto payment.pixQrCode: response.payment.pixQrCode.encodedImage, response.payment.pixQrCode.payload
 * 4. compatibilidade legada: response.qrCode, response.qrCodeBase64, response.pixPayload
 */
export function extractCanonicalPixQrData(response: any): CanonicalPixQrData {
  if (!response || typeof response !== "object") {
    return { encodedImage: "", payload: "", qrCodeBase64: "" };
  }

  // Se houver mensagem de erro ou falha explícita
  if (response.error && !response.payload && !response.encodedImage && !response.qrCode && !response.data) {
    return { encodedImage: "", payload: "", qrCodeBase64: "" };
  }

  // 1. Extração robusta de imagem base64 de qualquer variante
  const rawImage =
    response.encodedImage ||
    response.qrCodeBase64 ||
    response.pixQrCode?.encodedImage ||
    response.pixQrCode?.qrCodeBase64 ||
    response.payment?.pixQrCode?.encodedImage ||
    response.payment?.pixQrCode?.qrCodeBase64 ||
    response.qrCode?.encodedImage ||
    response.data?.encodedImage ||
    response.data?.qrCodeBase64 ||
    response.data?.pixQrCode?.encodedImage ||
    (typeof response.pixQrCode === "string" ? response.pixQrCode : "") ||
    "";

  // 2. Extração robusta de payload (código Copia e Cola)
  let rawPayload = "";
  if (typeof response.payload === "string" && response.payload.trim()) {
    rawPayload = response.payload.trim();
  } else if (typeof response.payloadStr === "string" && response.payloadStr.trim()) {
    rawPayload = response.payloadStr.trim();
  } else if (typeof response.pixPayload === "string" && response.pixPayload.trim()) {
    rawPayload = response.pixPayload.trim();
  } else if (response.pixQrCode?.payload && typeof response.pixQrCode.payload === "string") {
    rawPayload = response.pixQrCode.payload.trim();
  } else if (response.payment?.pixQrCode?.payload && typeof response.payment.pixQrCode.payload === "string") {
    rawPayload = response.payment.pixQrCode.payload.trim();
  } else if (response.data?.payload && typeof response.data.payload === "string") {
    rawPayload = response.data.payload.trim();
  } else if (response.data?.pixQrCode?.payload && typeof response.data.pixQrCode.payload === "string") {
    rawPayload = response.data.pixQrCode.payload.trim();
  } else if (response.qrCode?.payload && typeof response.qrCode.payload === "string") {
    rawPayload = response.qrCode.payload.trim();
  } else if (typeof response.qrCode === "string" && response.qrCode.trim()) {
    rawPayload = response.qrCode.trim();
  }

  let cleanBase64 = String(rawImage || "").trim();
  if (cleanBase64.startsWith("data:")) {
    const commaIndex = cleanBase64.indexOf(",");
    if (commaIndex !== -1) {
      cleanBase64 = cleanBase64.substring(commaIndex + 1).trim();
    }
  }

  const payloadStr = String(rawPayload || "").trim();

  return {
    encodedImage: cleanBase64,
    payload: payloadStr,
    qrCodeBase64: cleanBase64,
  };
}

/**
 * Calculates financial breakdown respecting the platform business rules:
 * - Commission (e.g., 4%) is calculated on the TOTAL appointment value, but deducted from the advance deposit.
 * - Gateway transaction fee (~0.99% on PIX) is allocated based on whoPaysMpFee ("platform" vs "arena").
 */
export function calculateBookingFinancials(
  totalPrice: number,
  advancePercentage: number = 50,
  overrideSettings?: Partial<SystemSettings>
): BookingFinancialBreakdown {
  const settings = { ...getSystemSettings(), ...overrideSettings };
  const commissionRate = settings.courtCommissionRate ?? settings.platformCommissionRate ?? 4.0;
  const effectiveFixedFee = settings.allFeesPercentageMode ? 0 : (settings.platformFixedFee ?? 0);
  const whoPaysMpFee = settings.whoPaysMpFee || "platform";

  // 1. Calculate App Commission over the TOTAL appointment price (in % mode)
  const appCommissionOnTotal = Math.round((totalPrice * (commissionRate / 100) + effectiveFixedFee) * 100) / 100;

  // 2. Calculate the advance required from athlete
  let advanceRequired = Math.round((totalPrice * (advancePercentage / 100)) * 100) / 100;
  if (advanceRequired <= 0) {
    advanceRequired = totalPrice; // fallback if no advance specified
  }

  // 3. Calculate Gateway Fee (~0.99% on PIX transaction)
  const mpFeePercentage = 0.99;
  const mpFeeAmount = Math.round((advanceRequired * (mpFeePercentage / 100)) * 100) / 100;

  // 4. Calculate Net Distributions based on who pays the MP/Gateway Fee rule
  let arenaNetFromAdvance = 0;
  let appNetCommission = 0;

  if (whoPaysMpFee === "platform") {
    arenaNetFromAdvance = Math.max(0, Math.round((advanceRequired - appCommissionOnTotal) * 100) / 100);
    appNetCommission = Math.max(0, Math.round((appCommissionOnTotal - mpFeeAmount) * 100) / 100);
  } else {
    arenaNetFromAdvance = Math.max(0, Math.round((advanceRequired - appCommissionOnTotal - mpFeeAmount) * 100) / 100);
    appNetCommission = appCommissionOnTotal;
  }

  // 5. Remaining balance to pay at reception
  const remainingBalanceAtArena = Math.max(0, Math.round((totalPrice - advanceRequired) * 100) / 100);

  return {
    totalPrice,
    advancePercentage,
    advanceRequired,
    appCommissionOnTotal,
    mpFeePercentage,
    mpFeeAmount,
    whoPaysMpFee,
    appNetCommission,
    arenaGrossFromAdvance: advanceRequired,
    arenaNetFromAdvance,
    remainingBalanceAtArena,
    commissionRate,
    commissionRatePercent: commissionRate,
    fixedFee: effectiveFixedFee,
  };
}

/**
 * Calculates remaining seconds until expiration timestamp (expiresAt)
 */
export function calculateRemainingSeconds(expiresAt?: string | number | null): number {
  if (!expiresAt) return 0;
  const targetTime = typeof expiresAt === "number" ? expiresAt : new Date(expiresAt).getTime();
  if (isNaN(targetTime)) return 0;
  const diffMs = targetTime - Date.now();
  return Math.max(0, Math.floor(diffMs / 1000));
}

/**
 * Formats seconds into MM:SS display format (e.g., 05:00, 04:59)
 */
export function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

/**
 * Gets effective commission rate with fallback to default (4%)
 */
export function getEffectiveCommissionRate(options?: { customCommissionRate?: number }): number {
  return options?.customCommissionRate ?? 4.0;
}

/**
 * Generates ISO string timestamps for createdAt and 5-minute expiresAt
 */
export function generatePixExpirationTimestamps(): { createdAt: string; expiresAt: string } {
  const nowMs = Date.now();
  const createdAt = new Date(nowMs).toISOString();
  const expiresAt = new Date(nowMs + 5 * 60 * 1000).toISOString(); // 5 minutes validity
  return { createdAt, expiresAt };
}
